import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { SnackbarKey, useSnackbar } from 'notistack';

import { Button } from '@mui/material';

import { offerQueryKeys } from '~/hooks/apis/offers/useGetOffer';

import OffersService from '../../../../services/OffersService';
import { reportError } from '../../../../utils/reportError';

const UNDO_DELAY_MS = 5000;

interface UseUndoableDeleteOptions {
  offerId: string;
  /** Human-readable label for the snackbar message, e.g. "All 3 schedules for LE" */
  label: string;
  /** Returns the list of schedule IDs to delete. Evaluated at confirm-time so it
   *  always uses the latest schedules without needing them in a dependency array. */
  getIdList: () => Array<number>;
  /** Called after the API call succeeds */
  onSuccess: () => void;
  /** Called if the API call fails */
  onError: (error: Error) => void;
}

/**
 * Encapsulates the undo pattern for destructive bulk-delete actions.
 *
 * Returns `{ confirm, isDeleting, isUndoWindowOpen, cancel }`:
 * - `confirm()` – call when the user clicks the final "Delete" button in a confirmation dialog.
 *   It immediately dismisses the dialog, shows a 5-second undo snackbar, and – if the user
 *   doesn't click Undo – optimistically removes the schedules from the React Query cache before
 *   firing the API call.
 * - `isDeleting` – true from confirm() until the API call settles (covers the undo window *and*
 *   the in-flight request). Use this to disable other actions for the whole operation.
 * - `isUndoWindowOpen` – true only for the 5-second window before the delete actually commits.
 *   `cancel()` is only meaningful while this is true — once it flips false the schedules are
 *   already being deleted server-side, so an inline "Undo" control should stop being offered
 *   (render a disabled/"Deleting…" state off `isDeleting` instead). Split from `isDeleting`
 *   because otherwise `cancel()` stays callable for the entire in-flight request: it would reset
 *   `isDeleting` to false — implying the delete was cancelled — while the already-committed
 *   optimistic cache update and API call keep running regardless.
 * - `cancel()` – aborts the pending delete and closes the snackbar, identical to clicking the
 *   snackbar's own Undo action. Exposed so callers can render an inline "Undo" affordance next to
 *   their own busy/deleting UI instead of relying solely on the snackbar (easy to miss, e.g. when
 *   a dialog is open over most of the screen).
 */
export function useUndoableDelete({ offerId, label, getIdList, onSuccess, onError }: UseUndoableDeleteOptions) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snackbarKeyRef = useRef<SnackbarKey | undefined>(undefined);
  const [isUndoWindowOpen, setIsUndoWindowOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }
    };
  }, []);

  const cancel = useCallback(() => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    if (snackbarKeyRef.current !== undefined) {
      closeSnackbar(snackbarKeyRef.current);
      snackbarKeyRef.current = undefined;
    }
    setIsUndoWindowOpen(false);
    setIsDeleting(false);
  }, [closeSnackbar]);

  const confirm = useCallback(() => {
    const idList = getIdList();
    if (idList.length === 0) {
      return;
    }

    setIsUndoWindowOpen(true);
    setIsDeleting(true);

    snackbarKeyRef.current = enqueueSnackbar(`${label} deleted`, {
      variant: 'info',
      action: () => (
        <Button size="small" color="inherit" onClick={cancel}>
          Undo
        </Button>
      ),
      autoHideDuration: UNDO_DELAY_MS,
    });

    undoTimerRef.current = setTimeout(() => {
      setIsUndoWindowOpen(false);
      closeSnackbar(snackbarKeyRef.current);
      snackbarKeyRef.current = undefined;
      queryClient.setQueryData(offerQueryKeys.detail(offerId), (old: App.Offer | undefined) => {
        if (!old) {
          return old;
        }
        return { ...old, brand_schedules: old.brand_schedules.filter((s) => !idList.includes(s.id)) };
      });
      OffersService.deleteScheduleList(offerId, { idList })
        .then(() => {
          setIsDeleting(false);
          onSuccess();
        })
        .catch((e) => {
          setIsDeleting(false);
          onError(e);
          reportError(e);
        });
    }, UNDO_DELAY_MS);
  }, [offerId, label, getIdList, onSuccess, onError, enqueueSnackbar, closeSnackbar, cancel, queryClient]);

  return { confirm, isDeleting, isUndoWindowOpen, cancel };
}

interface UseUndoableCallbackOptions {
  /** Human-readable label for the snackbar message */
  label: string;
  /** The async action to execute after the undo window expires */
  onConfirm: () => Promise<void>;
}

/**
 * Lighter variant for cases where the caller provides its own async action
 * (e.g. the global "Clear Schedules" button that calls migrateScheduleData).
 */
export function useUndoableCallback({ label, onConfirm }: UseUndoableCallbackOptions) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }
    };
  }, []);

  const confirm = useCallback(() => {
    setIsDeleting(true);

    const snackbarKey = enqueueSnackbar(label, {
      variant: 'info',
      action: (key) => (
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
            closeSnackbar(key);
            setIsDeleting(false);
          }}
        >
          Undo
        </Button>
      ),
      autoHideDuration: UNDO_DELAY_MS,
    });

    undoTimerRef.current = setTimeout(async () => {
      closeSnackbar(snackbarKey);
      await onConfirm();
      setIsDeleting(false);
    }, UNDO_DELAY_MS);
  }, [label, onConfirm, enqueueSnackbar, closeSnackbar]);

  return { confirm, isDeleting };
}
